import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { useAgentExecutionClient } from "src/agents/queries/execution";
import { DecisionEnvironment } from "src/api/types";
import type {
  AgentHistoryResponse,
  SessionResponse,
  SessionStatus,
  SortDirection,
  SortField,
} from "src/clients/agent-management";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UseAgentHistoryParams = {
  agentId: string;
  environment: DecisionEnvironment;
  from: string; // ISO string
  to: string; // ISO string
  sort: { field: SortField; direction: SortDirection };
  status?: SessionStatus[];
  agent_version_id?: string[];
  origin?: string[];
  model?: string[];
  page_size: number;
  searchSessionId?: string | null;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PREFIX_CACHE_KEY = "agent-history";

const historyKeys = {
  all: (wsId: string) => [PREFIX_CACHE_KEY, wsId] as const,
  sessions: (wsId: string, ...params: unknown[]) =>
    [...historyKeys.all(wsId), "sessions", ...params] as const,
  sessionSearch: (wsId: string, ...params: unknown[]) =>
    [...historyKeys.all(wsId), "session-search", ...params] as const,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useAgentHistory = (params: UseAgentHistoryParams) => {
  const {
    agentId,
    environment,
    from,
    to,
    sort,
    status,
    agent_version_id: version,
    origin,
    model,
    page_size,
    searchSessionId,
  } = params;

  const { client, workspace } = useAgentExecutionClient();
  const wsId = workspace?.id ?? "";
  const isSearching = !!searchSessionId;

  const apiEnvironment =
    environment === DecisionEnvironment.LIVE ? "production" : "sandbox";

  const listQuery = useInfiniteQuery({
    queryKey: historyKeys.sessions(
      wsId,
      agentId,
      environment,
      from,
      to,
      sort?.field,
      sort?.direction,
      status,
      version,
      origin,
      model,
      page_size,
    ),
    queryFn: async ({ pageParam }): Promise<AgentHistoryResponse> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      const response =
        await client.listSessionsAgentsManagementApiV0AgentsAgentIdSessionsGet({
          agentId,
          environment: apiEnvironment,
          from,
          to,
          cursor: pageParam,
          pageSize: page_size,
          sortField: sort?.field,
          sortDirection: sort?.direction,
          status: status?.length ? status.join(",") : undefined,
          agentVersionId: version?.length ? version.join(",") : undefined,
          origin: origin?.length ? origin.join(",") : undefined,
          model: model?.length ? model.join(",") : undefined,
        });
      return response.data;
    },
    enabled: !!client && !isSearching,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    select: (data) => ({
      sessions: data.pages.flatMap((p) => p.sessions),
      metrics: data.pages[data.pages.length - 1].metrics,
      total_count: data.pages[data.pages.length - 1].total_count,
    }),
  });

  const searchQuery = useQuery({
    queryKey: historyKeys.sessionSearch(
      wsId,
      agentId,
      apiEnvironment,
      searchSessionId,
    ),
    queryFn: async (): Promise<SessionResponse | null> => {
      if (!client) {
        throw new Error("Agent execution client not available");
      }
      if (!searchSessionId) {
        throw new Error("Session ID is required");
      }
      try {
        const response =
          await client.getSessionAgentsManagementApiV0SessionsSessionIdGet({
            sessionId: searchSessionId,
            includeMessages: false,
          });
        if (
          response.data.agent_id !== agentId ||
          response.data.environment !== apiEnvironment
        ) {
          return null;
        }
        return response.data;
      } catch (error) {
        if (
          isAxiosError(error) &&
          [404, 422].includes(error.response?.status ?? 0)
        ) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!client && isSearching,
  });

  if (isSearching) {
    return {
      data: searchQuery.data
        ? {
            sessions: [searchQuery.data],
            metrics: {},
            total_count: 1,
          }
        : undefined,
      isLoading: searchQuery.isLoading,
      isError: searchQuery.isError,
      fetchNextPage: () => {},
      isFetchingNextPage: false,
      refetch: searchQuery.refetch,
      isRefetching: searchQuery.isRefetching,
    };
  } else {
    return listQuery;
  }
};
